export const AUDIT_LOG_COLUMN_IDS = ["occurredAt", "actorLabel", "entityLabel", "rootLabel", "ip"] as const;

export type AuditLogColumnId = (typeof AUDIT_LOG_COLUMN_IDS)[number];

export type AuditLogColumnVisibility = Record<AuditLogColumnId, boolean>;

export const AUDIT_LOG_DENSITIES = ["compact", "comfortable"] as const;

export type AuditLogDensity = (typeof AUDIT_LOG_DENSITIES)[number];

export const AUDIT_LOG_GRID_KEY = "audit-log";

export const defaultAuditLogVisibility = (): AuditLogColumnVisibility => ({
    occurredAt: true,
    actorLabel: true,
    entityLabel: true,
    rootLabel: true,
    ip: false,
});

const isAuditLogColumnId = (value: string): value is AuditLogColumnId =>
    (AUDIT_LOG_COLUMN_IDS as readonly string[]).includes(value);

export const visibilityFromEntries = (entries: Iterable<[string, boolean]>): AuditLogColumnVisibility => {
    const stored = new Map<AuditLogColumnId, boolean>();

    for (const [id, visible] of entries) {
        if (isAuditLogColumnId(id)) {
            stored.set(id, visible);
        }
    }

    if (stored.size !== AUDIT_LOG_COLUMN_IDS.length) {
        return defaultAuditLogVisibility();
    }

    return AUDIT_LOG_COLUMN_IDS.reduce<AuditLogColumnVisibility>(
        (visibility, id) => ({ ...visibility, [id]: stored.get(id) === true }),
        defaultAuditLogVisibility(),
    );
};

export const visibilityToEntries = (visibility: AuditLogColumnVisibility): [AuditLogColumnId, boolean][] =>
    AUDIT_LOG_COLUMN_IDS.map((id) => [id, visibility[id]]);

export const visibleAuditLogColumns = (visibility: AuditLogColumnVisibility): AuditLogColumnId[] =>
    AUDIT_LOG_COLUMN_IDS.filter((id) => visibility[id]);

export const applyVisibleAuditLogColumns = (selected: AuditLogColumnId[]): AuditLogColumnVisibility =>
    AUDIT_LOG_COLUMN_IDS.reduce<AuditLogColumnVisibility>(
        (visibility, id) => ({ ...visibility, [id]: selected.includes(id) }),
        defaultAuditLogVisibility(),
    );

export const isAuditLogDensity = (value: unknown): value is AuditLogDensity =>
    typeof value === "string" && (AUDIT_LOG_DENSITIES as readonly string[]).includes(value);
